/**
 * Agent / walkthrough episodes — structured memory of what Zana just did.
 * Lives on chat messages so Ask payload v2 can send them with history.
 */

export type ZanaAgentKind = 'foreground' | 'background' | 'walkthrough';

export interface ZanaEpisode {
  type: 'agent_episode';
  agentId: string;
  name: string;
  kind: ZanaAgentKind;
  goal: string;
  topic?: string;
  tabsVisited: string[];
  tasksDone: string[];
  summary: string;
  estimateMs?: number;
  startedAt: number;
  endedAt: number;
  report?: { changed: boolean; lines: string[] };
}

export function newAgentId(): string {
  return `ag_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Deterministic mini-Zana names from topic / goal. */
export const AGENT_SELF_NAMES: Record<string, string> = {
  fuel: 'Fuel Desk',
  money: 'Fuel Desk',
  alerts: 'Alert Sweep',
  highRisk: 'Alert Sweep',
  routes: 'Route Desk',
  drivers: 'Driver Desk',
  accounts: 'Accounts Desk',
  invite_driver: 'Invite Guide',
  ground_bus_docs: 'Papers Check',
  maintenance: 'Papers Check',
  trips: 'Trips Desk',
  liveMap: 'Live Map Desk',
  monthlyReport: 'Report Runner',
  fuelPrices: 'Fuel Prices',
  fleet: 'Fleet Tour',
};

export function selfNameForTopic(topic: string | null | undefined, fallback = 'Desk Agent'): string {
  const t = String(topic || '').trim();
  if (t && AGENT_SELF_NAMES[t]) return AGENT_SELF_NAMES[t];
  return fallback;
}

/** One-line chat text for an episode (also sent in Ask history). */
export function episodeToChatText(ep: ZanaEpisode): string {
  const changed = ep.report?.changed ? ' (changes made)' : '';
  return `[Episode · Zana · ${ep.name}] ${ep.summary}${changed}`;
}

/** Pull recent episodes from chat messages (structured or bracket lines). */
export function episodesFromChat(
  messages: Array<{ from?: string; text?: string; episode?: ZanaEpisode }>,
  limit = 3,
): ZanaEpisode[] {
  const out: ZanaEpisode[] = [];
  for (let i = messages.length - 1; i >= 0 && out.length < limit; i -= 1) {
    const m = messages[i];
    if (m?.episode && m.episode.type === 'agent_episode') {
      out.push(m.episode);
      continue;
    }
    const t = String(m?.text || '');
    const match = t.match(/^\[Episode · Zana · ([^\]]+)\] (.+)$/s);
    if (match) {
      out.push({
        type: 'agent_episode',
        agentId: `legacy_${i}`,
        name: match[1],
        kind: 'walkthrough',
        goal: match[2].slice(0, 120),
        tabsVisited: [],
        tasksDone: [],
        summary: match[2].slice(0, 400),
        startedAt: 0,
        endedAt: 0,
      });
    }
  }
  return out.reverse();
}

export function formatEpisodesForAsk(episodes: ZanaEpisode[]): string {
  if (!episodes.length) return '';
  return episodes
    .map((e) => `- ${e.name}: ${e.summary}${e.topic ? ` [topic=${e.topic}]` : ''}`)
    .join('\n');
}
